import { OverlayContainer } from '@angular/cdk/overlay';
import { NgOptimizedImage } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import {
  Component,
  OnInit,
  Input,
  HostListener,
  ViewChild,
  Output,
  ElementRef,
  EventEmitter,
  inject,
  WritableSignal,
  signal,
  OnDestroy,
  ChangeDetectionStrategy,
  input
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { MatBadgeModule } from '@angular/material/badge';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatListModule } from '@angular/material/list';
import { MatMenuModule, MatMenuTrigger } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { LibNotificationComponent } from './notification/notification.component';
import { NotificationService, ToastMessageService, SystemService, AppInsightsService } from '@k12/app-services';
import {
  ToastMessageType,
  HeaderItem,
  MenuItem,
  GlobalSearchResult,
  GlobalSearchSectionName,
  HouseholdSearchResult,
  ProviderDetails,
  SchoolSearchResult,
  StudentSearchResult
} from '@k12/models';
import { errorToastValue } from '@k12/utils';
import { catchError, debounceTime, filter, finalize, of, Subject, switchMap, takeUntil, tap } from 'rxjs';

const WORK_QUEUE_URL = '/work-queue';
const STUDENT_URL = '/student';

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    FormsModule,
    LibNotificationComponent,
    MatBadgeModule,
    MatButtonModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatListModule,
    MatMenuModule,
    MatTooltipModule,
    NgOptimizedImage
  ],
  selector: 'lib-header',
  styleUrl: './header.component.scss',
  templateUrl: './header.component.html'
})
export class LibHeaderComponent implements OnInit, OnDestroy {
  @ViewChild(MatMenuTrigger) trigger!: MatMenuTrigger;
  @ViewChild('notificationOverlay') notificationOverlayRef!: ElementRef<HTMLElement>;
  @ViewChild('notificationToggle') notificationToggleRef!: ElementRef<HTMLElement>;
  @ViewChild('searchPanel') searchPanelRef!: ElementRef<HTMLElement>;
  @ViewChild('searchToggle') searchToggleRef!: ElementRef<HTMLElement>;

  @Input() adminUrl: string = '';
  @Input() headerItems: HeaderItem[] = [
    {
      label: 'Search',
      icon: '../assets/images/search.svg',
      route: ''
    },
    {
      label: 'FAQ',
      icon: '../assets/images/faq.svg',
      route: 'https://www.k12.ncseaa.edu/families-of-awarded-students/esaplus-program/service-providers/',
      url: 'true'
    },
    {
      label: 'Alerts',
      icon: '../assets/images/alerts.svg',
      route: ''
    },
    {
      label: 'Messaging',
      icon: '../assets/images/messaging.svg',
      route: 'messages',
      badge: ''
    },
    {
      label: 'Profile',
      icon: '../assets/images/profile.svg',
      route: 'profile',
      dropdown: []
    }
  ];
  @Input() isAdmin: boolean = false;
  @Input() isProvider: boolean = false;
  @Input() topMenu: MenuItem[] | undefined;
  @Input() unreadMessageCount: number = 0;
  showHeaderButtons = input<boolean>(true);

  @Output() entraLogout = new EventEmitter<void>();

  private readonly overlayContainer = inject(OverlayContainer);

  @HostListener('window:scroll', ['$event'])
  onScroll(event: Event) {
    event.preventDefault();
    if (this.isProvider) {
      this.trigger?.closeMenu();
    }
  }

  @HostListener('document:click', ['$event'])
  onClickOutsideClose(event: MouseEvent) {
    const target = event.target as HTMLElement | null;

    const clickedInsideToast =
      !!target?.closest('p-toast') ||
      !!target?.closest('.p-toast') ||
      !!target?.closest('.toast-container') ||
      !!target?.closest('.mat-mdc-snack-bar-container');

    if (clickedInsideToast) {
      return;
    }

    if (this.showSearch()) {
      const searchPanelElement = this.searchPanelRef?.nativeElement;
      const searchToggleElement = this.searchToggleRef?.nativeElement;

      if (searchPanelElement && !searchPanelElement.contains(target) && !searchToggleElement.contains(target)) {
        this.clearSearch();
      }
    }

    if (this.showNotification()) {
      const notificationElement = this.notificationOverlayRef?.nativeElement;
      const notificationToggleElement = this.notificationToggleRef?.nativeElement;
      const overlay = this.overlayContainer.getContainerElement();
      const clickedInside = overlay?.contains(target) ?? false;

      if (!clickedInside && !notificationElement?.contains(target) && !notificationToggleElement?.contains(target)) {
        this.showNotification.set(false);
      }
    }
  }

  @HostListener('document:keydown.escape')
  onEscapePress() {
    this.clearSearch();
    this.showNotification.set(false);
  }

  public isLoggedIn: boolean = false;
  public isOpen: boolean = false;
  public listAllSearch: WritableSignal<GlobalSearchResult | null> = signal(null);
  public isSearching: WritableSignal<boolean> = signal(false);
  public providersCount: number = 0; // add after api call integrated
  public notificationCount: number = 0;
  public menuIcon: string = '../assets/images/menu.svg';
  public searchValue: string = '';
  public showNotification: WritableSignal<boolean> = signal(false);
  public showSearch: WritableSignal<boolean> = signal(false);
  public userMenu: MenuItem[] | undefined;
  private readonly destroy$ = new Subject<void>();
  private readonly notificationService = inject(NotificationService);
  private readonly router = inject(Router);
  private readonly searchDestroy$ = new Subject<void>();
  private readonly searchSubject = new Subject<string>();
  private readonly systemService = inject(SystemService);
  private readonly toastMessageService = inject(ToastMessageService);
  private readonly appInsightsService = inject(AppInsightsService);

  public ngOnInit(): void {
    this.userMenu = [{ label: 'Logout' }];
    // Internal BehaviorSubject; cannot error by construction, so no error handler is needed.
    this.systemService.unreadMessageCount$
      .pipe(
        takeUntil(this.searchDestroy$),
        tap((count) => (this.unreadMessageCount = count))
      )
      .subscribe();
    this.searchCall();
    // Internal BehaviorSubject; cannot error by construction, so no error handler is needed.
    this.notificationService.notificationCount$
      .pipe(takeUntil(this.destroy$))
      .subscribe((response) => (this.notificationCount = response));
  }

  public ngOnDestroy(): void {
    this.searchDestroy$.next();
    this.searchDestroy$.complete();
    this.destroy$.next();
    this.destroy$.complete();
  }

  public get unreadMessagesCount(): number {
    return this.unreadMessageCount;
  }

  public cmdSwitch(item: string): void {
    if (item === 'Logout') {
      this.logoutEntra();
    }
  }

  public hidePanel(): void {
    this.showNotification.set(false);
  }

  public logoutEntra(): void {
    this.entraLogout.emit();
  }

  public navigatePage(route: string | undefined, url?: string | undefined, label?: string): void {
    if (label === 'Search') {
      this.showSearch.set(true);
    } else {
      this.clearSearch();
      if (route) {
        this.router.navigate(['/' + route]);
      }
      if (url) {
        window.open(route);
      }
    }
  }

  public navigateToProviderAccount(accountId: string): void {
    this.router.navigate([WORK_QUEUE_URL, accountId], { queryParams: { partnerTypeId: 2 } });
    this.clearSearch();
  }

  public onSearch(): void {
    const query = this.searchValue?.trim() ?? '';
    if (!query) {
      this.listAllSearch.set(null);
      this.isSearching.set(false);
      this.searchSubject.next('');
      return;
    }
    this.searchSubject.next(query);
  }

  public showNotificationCenter(): void {
    this.showNotification.set(true);
  }

  public userLogout(): void {
    this.isLoggedIn = !this.isLoggedIn;
  }

  public clearSearch(): void {
    this.showSearch.set(false);
    this.searchValue = '';
    this.listAllSearch.set(null);
    this.isSearching.set(false);
  }

  public handleNavigation(event: { route?: string; url?: string; label?: string }): void {
    this.navigatePage(event.route, event.url, event.label);
  }

  public onSearchValueChange(value: string): void {
    this.searchValue = value;
    this.onSearch();
  }

  private searchCall(): void {
    this.searchSubject
      .pipe(
        debounceTime(300),
        tap((query: string) => {
          const trimmed = query.trim();
          if (!trimmed) {
            this.listAllSearch.set(null);
            this.isSearching.set(false);
            return;
          }
          if (trimmed.length < 3) {
            this.listAllSearch.set(null);
            this.isSearching.set(false);
            return;
          }
          this.isSearching.set(true);
        }),
        filter((query: string) => query.trim().length >= 3),
        switchMap((query: string) =>
          this.systemService.getGlobalSearch(query.trim(), this.adminUrl).pipe(
            // Contain the error inside the inner observable. If it propagated to the outer
            // subscribe it would terminate the searchSubject pipeline, leaving the search box
            // unresponsive until a page refresh. Returning null here keeps the stream alive so
            // the next keystroke can retry.
            catchError((err: HttpErrorResponse) => {
              this.toastMessageService.showToast(ToastMessageType.Error, errorToastValue(err));
              return of(null);
            }),
            finalize(() => this.isSearching.set(false))
          )
        ),
        takeUntil(this.searchDestroy$)
      )
      // Upstream catchError already handles failures (toast + null fallback); cannot error here.
      .subscribe({
        next: (resp: GlobalSearchResult | null) => {
          this.listAllSearch.set(resp);
        }
      });
  }

  public loadMore(section: GlobalSearchSectionName): void {
    const searchText = this.searchValue?.trim();
    if (!searchText) {
      return;
    }
    const currentSection = this.listAllSearch()?.[section];
    if (!currentSection?.hasMore) {
      return;
    }
    this.systemService.getGlobalSearch(searchText, this.adminUrl, section, currentSection.items.length, 3).subscribe({
      next: (response: GlobalSearchResult) => {
        const nextSection = response[section];
        if (!nextSection) {
          return;
        }
        this.listAllSearch.update((current) => ({
          ...current,
          [section]: {
            ...nextSection,
            items: [...(current?.[section]?.items ?? []), ...(nextSection.items ?? [])]
          }
        }));
      },
      error: (error: unknown) => this.appInsightsService.log(error instanceof Error ? error : new Error(String(error)))
    });
  }

  public navigateTo(section: GlobalSearchSectionName, itemId: string): void {
    switch (section) {
      case 'provider':
        this.router.navigate([WORK_QUEUE_URL, itemId], { queryParams: { partnerTypeId: 2 } });
        break;
      case 'household':
        this.router.navigate([WORK_QUEUE_URL, itemId], { queryParams: { partnerTypeId: 3 } });
        break;
      case 'student':
        this.router.navigate([STUDENT_URL, itemId]);
        break;
      case 'school':
        this.router.navigate([WORK_QUEUE_URL, itemId], { queryParams: { partnerTypeId: 1 } });
        break;
    }
    this.clearSearch();
  }

  public providerItems(): ProviderDetails[] {
    return this.listAllSearch()?.provider?.items ?? [];
  }

  public householdItems(): HouseholdSearchResult[] {
    return this.listAllSearch()?.household?.items ?? [];
  }

  public studentItems(): StudentSearchResult[] {
    return this.listAllSearch()?.student?.items ?? [];
  }

  public schoolItems(): SchoolSearchResult[] {
    return this.listAllSearch()?.school?.items ?? [];
  }

  public hasHouseholdSectionResults(): boolean {
    return this.householdItems().length > 0 || this.studentItems().length > 0;
  }

  public householdSectionCount(): number {
    return (this.listAllSearch()?.household?.items.length ?? 0) + (this.listAllSearch()?.student?.items.length ?? 0);
  }
}
